import React, { useEffect, useState } from 'react';
import { Loan } from '../types';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { MoreHorizontal, Clock, User, Phone, Lock, Unlock, Timer } from 'lucide-react';
import { motion } from 'framer-motion';
interface RetentionQueueProps {
  loans: Loan[];
  onActionClick: (loan: Loan) => void;
  onClaim: (loanId: string) => void;
  onRelease: (loanId: string) => void;
  currentUser: string;
}
export function RetentionQueue({
  loans,
  onActionClick,
  onClaim,
  onRelease,
  currentUser
}: RetentionQueueProps) {
  // Force re-render every second for the countdown timer
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const getDaysInRetention = (dateEntered: string) => {
    const start = new Date(dateEntered);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const getUrgencyColor = (days: number) => {
    if (days > 5) return 'text-rose-600 bg-rose-50 border-rose-100';
    if (days > 2) return 'text-accent-600 bg-accent-50 border-accent-100';
    return 'text-emerald-600 bg-emerald-50 border-emerald-100';
  };
  const getRemainingTime = (claimedAt: string) => {
    const claimTime = new Date(claimedAt).getTime();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const remainingMs = Math.max(0, claimTime + oneHour - now);
    const minutes = Math.floor(remainingMs / (1000 * 60));
    const seconds = Math.floor(remainingMs % (1000 * 60) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <Card noPadding className="overflow-hidden border-slate-200" data-id="element-41">
      <div className="overflow-x-auto" data-id="element-42">
        <table className="w-full text-left text-xs" data-id="element-43">
          <thead className="bg-brand-500 border-b border-brand-700" data-id="element-44">
            <tr data-id="element-45">
              <th className="px-4 py-2.5 font-semibold text-white" data-id="element-46">
                Loan Details
              </th>
              <th className="px-4 py-2.5 font-semibold text-white" data-id="element-47">
                Retention Reason
              </th>
              <th className="px-4 py-2.5 font-semibold text-white" data-id="element-48">
                Pushed By
              </th>
              <th className="px-4 py-2.5 font-semibold text-white" data-id="element-49">Timeline</th>
              <th className="px-4 py-2.5 font-semibold text-white text-right" data-id="element-50">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-51">
            {loans.length === 0 ? <tr data-id="element-52">
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-id="element-53">
                  No active loans in retention queue. Good job!
                </td>
              </tr> : loans.map((loan, index) => {
            const days = getDaysInRetention(loan.dateEntered);
            return <motion.tr key={loan.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.03
            }} className="hover:bg-brand-50/50 transition-colors group" data-id="element-54">
                    <td className="px-4 py-2.5" data-id="element-55">
                      <div className="font-medium text-black text-sm leading-tight" data-id="element-56">
                        {loan.borrowerName}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono" data-id="element-57">
                        {loan.loanNumber}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500" data-id="element-58">
                        {loan.claimedBy === currentUser && <span className="inline-flex items-center gap-0.5" data-id="element-59">
                            <Phone className="h-2.5 w-2.5" data-id="element-60" />{' '}
                            {loan.borrowerPhone}
                          </span>}
                        <span className="inline-flex items-center gap-0.5" data-id="element-61">
                          <User className="h-2.5 w-2.5" data-id="element-62" /> {loan.loanOfficer}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5" data-id="element-63">
                      <div className="max-w-xs" data-id="element-64">
                        <Badge variant="destructive" className="mb-0.5 text-[10px] px-1.5 py-0" data-id="element-65">
                          Needs Action
                        </Badge>
                        <p className="text-slate-700 truncate text-xs" title={loan.retentionComment} data-id="element-66">
                          {loan.retentionComment}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-2.5" data-id="element-67">
                      <div className="flex items-center gap-1.5" data-id="element-68">
                        <div className="h-5 w-5 rounded-full bg-brand-100 flex items-center justify-center text-[9px] font-bold text-brand-600" data-id="element-69">
                          {loan.pushedBy.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <span className="text-slate-600" data-id="element-70">{loan.pushedBy}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5" data-id="element-71">
                      <div className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${getUrgencyColor(days)}`} data-id="element-72">
                        <Clock className="h-2.5 w-2.5 mr-1" data-id="element-73" />
                        {days}d
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-74">
                        {new Date(loan.dateEntered).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right" data-id="element-75">
                      {!loan.claimedBy ? <Button size="sm" variant="outline" onClick={() => onClaim(loan.id)} className="opacity-0 group-hover:opacity-100 transition-opacity h-7 text-[11px] px-2.5" data-id="element-76">
                          <Lock className="h-3 w-3 mr-1.5" data-id="element-77" />
                          Claim File
                        </Button> : loan.claimedBy === currentUser ? <div className="flex items-center justify-end gap-2" data-id="element-78">
                          <div className="flex items-center text-[11px] font-mono text-accent-600 bg-accent-50 px-2 py-1 rounded border border-accent-100" data-id="element-79">
                            <Timer className="h-3 w-3 mr-1" data-id="element-80" />
                            {loan.claimedAt && getRemainingTime(loan.claimedAt)}
                          </div>
                          <Button size="sm" variant="primary" onClick={() => onActionClick(loan)} className="h-7 text-[11px] px-2.5" data-id="element-81">
                            Take Action
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onRelease(loan.id)} className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" title="Release Claim" data-id="element-82">
                            <Unlock className="h-3.5 w-3.5" data-id="element-83" />
                          </Button>
                        </div> : <div className="flex items-center justify-end gap-2" data-id="element-84">
                          <Badge variant="secondary" className="font-normal text-[10px]" data-id="element-85">
                            Claimed by {loan.claimedBy.split(' ')[0]}
                          </Badge>
                          <div className="flex items-center text-[10px] font-mono text-slate-500" data-id="element-86">
                            <Timer className="h-3 w-3 mr-1" data-id="element-87" />
                            {loan.claimedAt && getRemainingTime(loan.claimedAt)}
                          </div>
                        </div>}
                    </td>
                  </motion.tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
}